import {
  signInWithCustomToken,
} from 'firebase/auth';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import {
  getFirestore,
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  serverTimestamp,
  type Firestore,
  type DocumentData,
  type QuerySnapshot,
  type DocumentSnapshot
} from 'firebase/firestore';
import type { FirebaseApp } from 'firebase/app';
import {
  authService as _canonicalAuthService,
  getAuthInstance as _canonicalGetAuthInstance,
  getAppInstance as _canonicalGetAppInstance,
} from './firebase-auth-service';
import { Capacitor } from '@capacitor/core';

// Module-level cache — populated by initializeFirebase() which delegates
// app + auth creation to the canonical auth service (firebase-auth-service.ts).
let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: any = null;
let firebaseMessaging: Messaging | null = null;
let firestoreDb: Firestore | null = null;

// Initialize Firebase — delegates app/auth to firebase-auth-service.ts (single source of truth)
// and independently initializes Messaging + Firestore.
const initializeFirebase = async () => {
  if (firebaseApp && firebaseAuth) {
    return { app: firebaseApp, auth: firebaseAuth, messaging: firebaseMessaging, firestore: firestoreDb };
  }

  // Get app and auth from the canonical auth service
  firebaseApp = await _canonicalGetAppInstance();
  firebaseAuth = await _canonicalGetAuthInstance();

  // Initialize messaging (web only, requires Notification API + service worker)
  if (typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator) {
    try {
      firebaseMessaging = getMessaging(firebaseApp);
      console.log('Firebase Messaging initialized');
    } catch (error) {
      console.warn('Firebase Messaging not supported or failed to initialize:', error);
    }
  }

  // Initialize Firestore for chat feature
  try {
    firestoreDb = getFirestore(firebaseApp);
    console.log('Firebase Firestore initialized');
  } catch (error) {
    console.warn('Firebase Firestore failed to initialize:', error);
  }

  return { app: firebaseApp, auth: firebaseAuth, messaging: firebaseMessaging, firestore: firestoreDb };
};

// Single initialization pattern
let initPromise: Promise<{ auth: any; app: FirebaseApp; messaging: Messaging | null; firestore: Firestore | null }> | null = null;

const init = async () => {
  if (!initPromise) {
    initPromise = initializeFirebase();
  }
  return initPromise;
};

// Track whether we've already bridged auth to prevent duplicate calls
let webAuthBridged = false;
let webAuthBridgePromise: Promise<void> | null = null;

/**
 * Check if web auth has been successfully bridged from native auth.
 * Used by components that need Firestore access to wait for the bridge.
 */
export function isWebAuthBridged(): boolean {
  return webAuthBridged;
}

/**
 * Wait for the web auth bridge to complete (if in progress).
 * Returns immediately if not on native platform or already bridged.
 */
export async function waitForAuthBridge(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  if (webAuthBridged) {
    return;
  }
  if (webAuthBridgePromise) {
    return webAuthBridgePromise;
  }
  // Bridge not started yet, trigger it
  return bridgeNativeAuthToWeb();
}

/**
 * Bridge native Firebase auth to web Firebase auth for Firestore access.
 * On native platforms, we need to sync the native auth session to the web SDK
 * so that Firestore security rules can see the authenticated user.
 */
async function bridgeNativeAuthToWeb(): Promise<void> {
  // Only needed on native platforms
  if (!Capacitor.isNativePlatform()) {
    console.log('🌉 [Auth Bridge] Not on native platform, skipping bridge');
    return;
  }

  // Log platform details for debugging
  const platform = Capacitor.getPlatform();
  console.log(`🌉 [Auth Bridge] Platform: ${platform}, isNative: ${Capacitor.isNativePlatform()}`);

  // Prevent duplicate calls
  if (webAuthBridged) {
    console.log('🌉 [Auth Bridge] Already bridged, skipping');
    return;
  }

  // If bridge is in progress, wait for it
  if (webAuthBridgePromise) {
    console.log('🌉 [Auth Bridge] Bridge in progress, waiting...');
    return webAuthBridgePromise;
  }

  webAuthBridgePromise = (async () => {
    try {
      console.log('🌉 [Auth Bridge] Starting native-to-web auth bridge...');

      // Get the native Firebase auth plugin
      console.log('🌉 [Auth Bridge] Importing FirebaseAuthentication plugin...');
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      console.log('🌉 [Auth Bridge] FirebaseAuthentication plugin imported');

      // First check if a user is currently signed in
      console.log('🌉 [Auth Bridge] Checking current user...');
      let currentUser: any = null;
      try {
        const currentUserResult = await FirebaseAuthentication.getCurrentUser();
        currentUser = currentUserResult?.user;
        console.log('🌉 [Auth Bridge] getCurrentUser result:', {
          hasUser: !!currentUser,
          uid: currentUser?.uid?.substring(0, 8) + '...',
          email: currentUser?.email,
        });
      } catch (getCurrentError: any) {
        console.warn('🌉 [Auth Bridge] getCurrentUser failed:', getCurrentError?.message);
      }

      if (!currentUser) {
        console.log('🌉 [Auth Bridge] No user currently signed in natively, skipping bridge');
        return;
      }

      // Get the ID token from native auth
      console.log('🌉 [Auth Bridge] Getting ID token...');
      const idTokenResult = await FirebaseAuthentication.getIdToken({ forceRefresh: false });
      console.log('🌉 [Auth Bridge] ID token result type:', typeof idTokenResult);
      console.log('🌉 [Auth Bridge] ID token result keys:', idTokenResult ? Object.keys(idTokenResult) : 'null');

      // Handle different possible result structures from the Capacitor plugin
      let idToken: string | null = null;
      if (typeof idTokenResult === 'string') {
        idToken = idTokenResult;
      } else if (idTokenResult?.token) {
        idToken = idTokenResult.token;
      } else if ((idTokenResult as any)?.value) {
        idToken = (idTokenResult as any).value;
      }

      if (!idToken) {
        console.log('🌉 [Auth Bridge] No native ID token available. Result:', JSON.stringify(idTokenResult));
        return;
      }

      console.log('🌉 [Auth Bridge] Got ID token, length:', idToken.length);

      // BUILD_MARKER: BUILD-1219-A - Unique identifier to verify build sync
      console.log('🌉 [Auth Bridge] BUILD-1219-A: Got native ID token, exchanging for custom token...');

      // Get the API base URL using the proper config helper that respects VITE_USE_LOCAL_API
      const { getApiUrl } = await import('../config/api');
      const baseUrl = getApiUrl();
      console.log('🌉 [Auth Bridge] BUILD-1219-A: Using API URL:', baseUrl);

      // Exchange ID token for custom token via backend with timeout
      const controller = new AbortController();
      const fetchTimeout = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      console.log('🌉 [Auth Bridge] BUILD-1219-A: About to fetch exchange-token endpoint...');

      let response: Response;
      try {
        console.log('🌉 [Auth Bridge] BUILD-1219-A: Fetching now...');
        response = await fetch(`${baseUrl}/api/auth/exchange-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ idToken }),
          signal: controller.signal,
        });
        console.log('🌉 [Auth Bridge] BUILD-1219-A: Fetch completed! Status:', response.status);
      } catch (fetchError: any) {
        clearTimeout(fetchTimeout);
        console.error('🌉 [Auth Bridge] BUILD-1219-A: Fetch failed!', fetchError.name, fetchError.message);
        if (fetchError.name === 'AbortError') {
          throw new Error('Token exchange request timed out after 10s');
        }
        throw fetchError;
      }
      clearTimeout(fetchTimeout);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('🌉 [Auth Bridge] BUILD-1219-A: Response not OK:', response.status, errorData);
        throw new Error(`Token exchange failed: ${errorData.error || response.statusText}`);
      }

      console.log('🌉 [Auth Bridge] BUILD-1219-A: Parsing response JSON...');
      const { customToken, uid } = await response.json();
      console.log('🌉 [Auth Bridge] BUILD-1219-A: Response parsed successfully');
      console.log('🌉 [Auth Bridge] Got custom token for user:', uid);
      console.log('🌉 [Auth Bridge] Custom token length:', customToken?.length);

      // Sign into web Firebase auth with the custom token
      // BUILD_MARKER: BUILD-1219-D - Using in-memory persistence initialized at startup
      console.log('🌉 [Auth Bridge] BUILD-1219-D: Getting Firebase auth instance...');
      const auth = await _canonicalGetAuthInstance();
      console.log('🌉 [Auth Bridge] BUILD-1219-D: Got auth instance (should be in-memory persistence)');

      console.log('🌉 [Auth Bridge] BUILD-1219-D: About to call signInWithCustomToken...');
      const signInPromise = signInWithCustomToken(auth, customToken);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('signInWithCustomToken timed out after 15s')), 15000)
      );

      try {
        console.log('🌉 [Auth Bridge] BUILD-1219-D: Waiting for signInWithCustomToken via Promise.race...');
        const result = await Promise.race([signInPromise, timeoutPromise]);
        console.log('🌉 [Auth Bridge] BUILD-1219-D: Promise.race resolved!');
        console.log('🌉 [Auth Bridge] BUILD-1219-D: Successfully signed into web Firebase auth');
        console.log('🌉 [Auth Bridge] BUILD-1219-D: Web auth user:', auth.currentUser?.uid);
        console.log('🌉 [Auth Bridge] BUILD-1219-D: Result:', result);
        webAuthBridged = true;
      } catch (signInError: any) {
        console.warn('🌉 [Auth Bridge] BUILD-1219-D: signInWithCustomToken failed or timed out:', signInError.message);
        console.warn('🌉 [Auth Bridge] BUILD-1219-D: Full error:', signInError);
        // Don't throw - app can still work with native auth
      }
    } catch (error) {
      console.error('🌉 [Auth Bridge] Failed to bridge auth:', error);
      // Don't throw - allow the app to continue even if bridge fails
    } finally {
      webAuthBridgePromise = null;
    }
  })();

  return webAuthBridgePromise;
}

// Delegate auth to the canonical service (firebase-auth-service.ts)
// to ensure a single AuthService instance across the entire app.
// Previously, this file had its own AuthService class which caused
// dual-singleton auth state conflicts on native (Capacitor) platforms.
export const authService = _canonicalAuthService;
export const getAuthInstance = _canonicalGetAuthInstance;
export const getAppInstance = _canonicalGetAppInstance;

export const getMessagingInstance = async () => {
  const { messaging } = await init();
  return messaging;
};

export const getFirestoreInstance = async () => {
  const { firestore } = await init();
  return firestore;
};

// Re-export Firestore utilities for chat feature
export {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  serverTimestamp,
  type DocumentData,
  type QuerySnapshot,
  type DocumentSnapshot
};

// Call init immediately but don't await at module level to start the process
console.log('🔥 Firebase module loaded, starting initialization...');
if ((window as any).iosDebugLog) {
  (window as any).iosDebugLog('🔥 Firebase module loaded, starting initialization...');
}
init().catch((error) => {
  console.error('🔥 Firebase init failed at module level:', error);
  if ((window as any).iosDebugLog) {
    (window as any).iosDebugLog(`🔥 Firebase init failed at module level: ${error}`);
  }
});
